import type { WhatsNewBody, WhatsNewEntry } from '../types';

/**
 * `YYYY-MM-DD-slug`, the entry file's name without `.ts`. The date is the day the change
 * reached users, which is what puts the entry in order and what the "new" rule counts from.
 */
const ENTRY_NAME = /^(\d{4})-(\d{2})-(\d{2})-([a-z0-9]+(?:-[a-z0-9]+)*)$/;

/** Null when `name` is not a well-formed entry name, or its date is not a real one. */
export function parseEntryName(name: string): { id: string; date: string } | null {
  const m = ENTRY_NAME.exec(name);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  // A real calendar date: Date rolls 2026-02-30 over to March, so round-trip it.
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) return null;
  return { id: name, date: `${m[1]}-${m[2]}-${m[3]}` };
}

/**
 * Turn the loaded entry files into entries, newest first. `modules` is what
 * `import.meta.glob('../entries/*.ts', { eager: true })` returns, keyed by path.
 *
 * Throws on a file name it cannot read instead of skipping it: an entry that silently does not
 * appear is exactly the failure a release note cannot afford, so a bad name fails every test
 * that loads the feed, and the `Build` job with it.
 */
export function buildEntries(modules: Record<string, { default: WhatsNewBody }>): WhatsNewEntry[] {
  const entries = Object.entries(modules).map(([path, mod]) => {
    const name = path.slice(path.lastIndexOf('/') + 1).replace(/\.ts$/, '');
    const parsed = parseEntryName(name);
    if (!parsed) {
      throw new Error(
        `What's new: "${name}" is not a valid entry name. Use YYYY-MM-DD-slug.ts `
        + `(lowercase letters, digits and hyphens, a real date).`,
      );
    }
    return { ...mod.default, ...parsed };
  });
  // Same day: by id, so the order does not depend on how the file system lists them.
  return entries.sort((a, b) => (a.date === b.date ? a.id.localeCompare(b.id) : a.date < b.date ? 1 : -1));
}

/** `2026-10-22` as "Oct 22, 2026". Read as a calendar date, never as an instant, so no time zone moves it a day. */
export function formatEntryDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric',
  });
}
